import { Component, OnInit, Input, Injectable, ViewContainerRef } from '@angular/core';
import {
  FileUploader,
  FileUploaderOptions,
  ParsedResponseHeaders,
  FileLikeObject,
} from 'ng2-file-upload';
import { MatLegacyDialogRef as MatDialogRef, MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ModalConfirmationComponent } from 'src/app/shared/components/modal-confirmation/modal-confirmation.component';
import { Image } from 'src/app/people/models/image';
import * as _ from 'lodash';
import { CloudinaryConfig } from 'src/app/shared/models/config/cloudinaryConfig';
import { AppConfigService } from 'src/app/shared/services/app-config.service';

const allowedMimeTypes = ['image/png', 'image/jpg', 'image/jpeg'];

@Component({
  selector: 'cloudinary-upload',
  templateUrl: 'cloudinary-upload.component.html',
  styleUrls: ['cloudinary-upload.component.scss'],
})
@Injectable()
export class CloudinaryUploadComponent implements OnInit {
  @Input()
  image: Image;
  @Input()
  imageChangedCallback: any;
  @Input()
  readOnly: boolean;
  @Input()
  alwaysShowIcons: boolean = false;
  @Input()
  errorCallback: any;
  uploader: FileUploader;

  oneMb = 1024 * 1024;
  maxFileSize = 10 * this.oneMb; // 10 MB
  shouldShowIcons: boolean = false;
  confirmationRef: MatDialogRef<ModalConfirmationComponent>;

  constructor(
    private dialog: MatDialog,
    private viewContainerRef: ViewContainerRef,
    private appConfigService: AppConfigService
  ) {}

  ngOnInit(): void {
    this.setupUploader();
  }

  removePicture() {
    let cmp = this;

    if (this.readOnly) {
      return;
    }

    cmp.confirmationRef = cmp.dialog.open(ModalConfirmationComponent, {
      viewContainerRef: cmp.viewContainerRef,
      data: { action: 'delete this photo', delete: true },
    });

    cmp.confirmationRef.afterClosed().subscribe((result) => {
      if (result) {
        this.image = new Image(null);

        if (cmp.imageChangedCallback) {
          cmp.imageChangedCallback(null);
        }
      }
      cmp.confirmationRef = null;
    });
  }

  setupUploader() {
    if (this.readOnly) {
      this.uploader = null;
      return;
    }

    const uploaderOptions: FileUploaderOptions = {
      url: `${this.cloudinaryConfig.baseApiUrl}${this.cloudinaryConfig.upload.cloud_name}/upload`,
      autoUpload: true,
      allowedMimeType: allowedMimeTypes,
      maxFileSize: this.maxFileSize,
      headers: [
        {
          name: 'X-Requested-With',
          value: 'XMLHttpRequest',
        },
      ],
    };
    this.uploader = new FileUploader(uploaderOptions);

    this.uploader.onBuildItemForm = (fileItem: any, form: FormData): any => {
      form.append('upload_preset', this.cloudinaryConfig.upload.upload_preset);
      form.append('file', fileItem);

      fileItem.withCredentials = false;
      return { fileItem, form };
    };

    this.uploader.onCompleteItem = (
      item: any,
      response: string,
      status: number,
      headers: ParsedResponseHeaders
    ) => {
      var parsed_response = JSON.parse(response);
      var image_id = parsed_response.public_id;
      this.image = new Image(image_id);

      if (this.imageChangedCallback) {
        this.imageChangedCallback(image_id);
      }
    };

    this.uploader.onWhenAddingFileFailed = (item, filter, options) =>
      this.onWhenAddingFileFailed(item, filter, options);
  }

  mouseEnter() {
    if (this.alwaysShowIcons) return;
    this.setShouldShowIcons(!this.readOnly);
  }

  mouseLeave() {
    if (this.alwaysShowIcons) return;
    this.setShouldShowIcons(false);
  }

  setShouldShowIcons(value: boolean) {
    this.shouldShowIcons = value;
  }

  shouldShowRemoveIcon(): boolean {
    return (
      (!this.readOnly && this.shouldShowIcons && this.hasImage()) ||
      (this.hasImage() && this.alwaysShowIcons)
    );
  }

  hasImage(): boolean {
    return !_.isNil(this.image) && !_.isNil(this.image.id);
  }

  shouldShowUploadPhoto(): boolean {
    return this.shouldShowIcons || (!this.hasImage() && this.alwaysShowIcons);
  }

  private onWhenAddingFileFailed(item: FileLikeObject, filter: any, options: any) {
    var errorMessage;

    switch (filter.name) {
      case 'fileSize':
        let fileSize = Math.round((item.size / this.oneMb) * 100) / 100;
        errorMessage = `Maximum upload size exceeded (${fileSize} MB of ${
          this.maxFileSize / this.oneMb
        } MB allowed).`;
        break;
      case 'mimeType':
        errorMessage = `Type "${
          item.type
        } is not allowed. Allowed types: "${allowedMimeTypes.join(', ')}".`;
        break;
      default:
        errorMessage = `Unknown error (filter is ${filter.name}).`;
    }

    this.errorCallback(errorMessage);
  }

  private get cloudinaryConfig(): CloudinaryConfig {
    return this.appConfigService.config.cloudinary;
  }
}
